import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Car, Gift, MapPin, ArrowRight, Sparkles, CheckCircle2, Users, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import i18n from '@/i18n';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CAMPAIGN_ID = 'launch-first-paid-10km-2026-11';

/**
 * Landing de l'offre de lancement Paris et petite couronne (ouverture le lundi 2 novembre 2026).
 *
 * Le crédit est attribué après confirmation du premier abonnement effectivement payé.
 * Il couvre une première course réelle, limitée à 10 km routiers, dans la limite
 * des places de la campagne configurée côté administration.
 */
const SaintDenis = () => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  // Force French (DGCCRF clarity argument)
  useEffect(() => {
    if (i18n.language !== 'fr') {
      i18n.changeLanguage('fr');
      try { localStorage.setItem('i18nextLng', 'fr'); } catch (_) {}
    }
  }, []);

  // Fetch live counter
  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/campaigns/${CAMPAIGN_ID}/status`);
      setCampaign(data);
    } catch (err) {
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Refresh toutes les 30 secondes pour effet "compteur live"
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const ctaHref = `/register/user?campaign=${encodeURIComponent(CAMPAIGN_ID)}`;

  const slotsRemaining = campaign?.slots_remaining ?? 0;
  const slotsTotal = campaign?.slots_total ?? 0;
  const slotsUsed = campaign?.slots_used ?? 0;
  const isFull = !loading && Boolean(campaign) && slotsRemaining === 0;
  const isExpired = !loading && Boolean(campaign?.expired);
  const isNotStarted = !loading && Boolean(campaign) && new Date(campaign.valid_from).getTime() > Date.now();
  const isUnavailable = !loading && Boolean(campaign) && !campaign.active && !isExpired && !isFull && !isNotStarted;
  const campaignMissing = !loading && !campaign;

  return (
    <div className="min-h-screen bg-[#09090B] text-white" data-testid="paris-inner-ring-launch-page">
      {/* Top nav */}
      <nav className="px-6 py-5 flex items-center justify-between border-b border-zinc-900">
        <Link to="/" className="flex items-center gap-2" data-testid="saint-denis-logo">
          <Car className="w-7 h-7 text-[#FFD60A]" />
          <span className="text-xl font-black tracking-tight">MÉTRO-TAXI</span>
        </Link>
        <Link to="/login" className="text-sm text-zinc-400 hover:text-white" data-testid="saint-denis-login-link">
          Se connecter
        </Link>
      </nav>

      {/* Hero */}
      <section className="px-6 py-16 md:py-24 max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD60A]/10 border border-[#FFD60A]/30 text-[#FFD60A] text-xs font-semibold mb-6">
            <MapPin className="w-3.5 h-3.5" />
            ZONE DE LANCEMENT — PARIS ET PETITE COURONNE (75, 92, 93, 94)
            <span className="text-zinc-500">·</span>
            LANCEMENT LUNDI 2 NOVEMBRE 2026
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
            Ta <span className="text-[#FFD60A]">1<sup>ère</sup> course offerte</span>
            <br />à Paris et en petite couronne.
          </h1>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl">
            Après confirmation du paiement de ton <span className="text-white font-semibold">premier abonnement Métro-Taxi</span>,
            reçois automatiquement une première course réelle offerte jusqu'à <span className="text-white font-semibold">10 km routiers</span>.
            Aucun code à saisir. Réservée aux adresses postales 75, 92, 93 et 94, dans la limite de 30 bénéficiaires et une seule fois par abonné.
          </p>

          {/* COMPTEUR LIVE — Effet rareté */}
          <div
            className="mt-10 bg-gradient-to-r from-[#FFD60A]/15 to-transparent border-l-4 border-[#FFD60A] p-6 rounded-sm max-w-2xl"
            data-testid="saint-denis-counter"
          >
            {loading ? (
              <p className="text-zinc-400">Chargement du compteur en direct…</p>
            ) : isExpired ? (
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-bold">La campagne Paris et petite couronne est terminée.</p>
                  <p className="text-sm text-zinc-400 mt-1">Tu peux toujours t'abonner pour profiter de nos tarifs et du covoiturage Métro-Taxi.</p>
                </div>
              </div>
            ) : campaignMissing ? (
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-bold">L’offre de lancement n’est pas encore ouverte.</p>
                  <p className="text-sm text-zinc-400 mt-1">La campagne et le nombre de places seront affichés ici dès que l’offre sera configurée.</p>
                </div>
              </div>
            ) : isNotStarted ? (
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-bold">Ouverture lundi 2 novembre 2026.</p>
                  <p className="text-sm text-zinc-400 mt-1">Le compteur indiquera le nombre de places dès l'ouverture de l'offre.</p>
                  <p className="text-sm text-zinc-300 mt-2" data-testid="launch-offer-pre-opening-notice">Un abonnement payé avant le lundi 2 novembre 2026 à 00 h 00 (heure de Paris) ne donne pas droit à la course offerte.</p>
                </div>
              </div>
            ) : isUnavailable ? (
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-bold">L’offre est temporairement indisponible.</p>
                  <p className="text-sm text-zinc-400 mt-1">Tu peux toujours souscrire à un abonnement Métro-Taxi aux tarifs habituels.</p>
                </div>
              </div>
            ) : isFull ? (
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-zinc-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-zinc-200 font-bold">Toutes les places de cette campagne ont été attribuées.</p>
                  <p className="text-sm text-zinc-400 mt-1">Tu peux quand même rejoindre Métro-Taxi avec un abonnement classique. Bienvenue.</p>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-zinc-400 uppercase tracking-wider font-semibold">PLACES RESTANTES</p>
                <p className="text-5xl md:text-6xl font-black text-[#FFD60A] mt-2" data-testid="saint-denis-slots-remaining">
                  {slotsRemaining}<span className="text-2xl text-zinc-500">/{slotsTotal}</span>
                </p>
                <p className="text-sm text-zinc-400 mt-3">
                  Déjà <span className="text-white font-semibold">{slotsUsed}</span> abonné{slotsUsed > 1 ? 's' : ''} ont sécurisé leur course offerte.
                </p>
                {slotsRemaining <= 10 && (
                  <p className="text-xs text-amber-400 mt-2 font-semibold animate-pulse">⚡ Dépêche-toi, ça part vite !</p>
                )}
              </div>
            )}
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
            <div className="bg-[#18181B] border border-zinc-800 p-5 rounded-sm">
              <Gift className="w-6 h-6 text-[#FFD60A] mb-3" />
              <p className="text-white font-bold">0 € sur ta 1ère course</p>
              <p className="text-sm text-zinc-500 mt-1">La plateforme prend en charge ta première course offerte.</p>
            </div>
            <div className="bg-[#18181B] border border-zinc-800 p-5 rounded-sm">
              <MapPin className="w-6 h-6 text-[#FFD60A] mb-3" />
              <p className="text-white font-bold">Jusqu'à 10 km</p>
              <p className="text-sm text-zinc-500 mt-1">Pour les trajets de proximité à Paris et dans les communes limitrophes.</p>
            </div>
            <div className="bg-[#18181B] border border-zinc-800 p-5 rounded-sm">
              <Users className="w-6 h-6 text-[#FFD60A] mb-3" />
              <p className="text-white font-bold">Covoiturage berline</p>
              <p className="text-sm text-zinc-500 mt-1">Place assise, climatisation, porte-à-porte. Vraiment confortable.</p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 max-w-2xl">
            <Link to={ctaHref}>
              <Button
                data-testid="saint-denis-cta-btn"
                disabled={loading || campaignMissing || isFull || isExpired || isNotStarted || isUnavailable}
                className="w-full sm:w-auto bg-[#FFD60A] text-black font-bold h-14 px-8 text-base hover:bg-[#E6C209] disabled:opacity-50"
              >
                {isFull || isExpired ? "Campagne terminée" : isNotStarted || campaignMissing || isUnavailable ? "Offre bientôt disponible" : "Choisir mon abonnement"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-zinc-500 mt-3">
              Abonnement à partir de 6,99 € / 24h. Le crédit est attribué après confirmation du premier paiement et s'applique à une course réelle dont la distance routière estimée ne dépasse pas 10 km.
            </p>
          </div>

          {/* How it works */}
          <div className="mt-16">
            <h2 className="text-lg font-bold mb-6">Comment ça marche ?</h2>
            <ol className="space-y-4 max-w-2xl">
              {[
                { t: "Crée ton compte", d: "Inscris-toi sur Métro-Taxi, puis choisis le forfait qui te convient." },
                { t: "Paie ton premier abonnement", d: "L'offre s'active uniquement après confirmation effective de ton premier paiement d'abonnement, entre le lundi 2 novembre 2026 à 00 h 00 et le lundi 16 novembre 2026 à 00 h 00 (heure de Paris). Un abonnement payé avant le 2 novembre n'y donne pas droit." },
                { t: "Réserve ta course offerte", d: "Une course réelle est offerte jusqu'à 10 km routiers. Si l'itinéraire dépasse cette limite ou si la distance routière ne peut pas être vérifiée, le crédit n'est pas utilisé." },
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FFD60A] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">{s.t}</p>
                    <p className="text-sm text-zinc-400">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-12 text-sm text-zinc-500">
            Tu es chauffeur VTC ? <Link to="/register/driver" className="text-[#FFD60A] hover:underline">Rejoins le réseau des pionniers</Link>.
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default SaintDenis;
